import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

export interface IntegrationConfig {
  id: string
  provider: string
  is_active: boolean
  sync_direction: string
  last_synced_at: string | null
  has_config: boolean
  created_at: string
  updated_at: string
}

export interface IntegrationSyncLog {
  id: string
  entity_type: string | null
  entity_id: string | null
  external_id: string | null
  action: string
  status: string
  request_payload: Record<string, unknown> | null
  response_payload: Record<string, unknown> | null
  error_message: string | null
  created_at: string
}

export function useIntegrations() {
  return useQuery({
    queryKey: ["integrations"],
    queryFn: () =>
      api.get<{ data: IntegrationConfig[] }>("/api/v1/integrations"),
  })
}

export function useIntegration(id: string) {
  return useQuery({
    queryKey: ["integrations", id],
    queryFn: () =>
      api.get<{ data: IntegrationConfig }>(`/api/v1/integrations/${id}`),
    enabled: !!id,
  })
}

export function useCreateIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      provider: string
      is_active?: boolean
      sync_direction?: string
      config?: Record<string, unknown>
    }) => api.post<{ data: IntegrationConfig }>("/api/v1/integrations", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  })
}

export function useUpdateIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: {
        is_active?: boolean
        sync_direction?: string
        config?: Record<string, unknown>
      }
    }) =>
      api.patch<{ data: IntegrationConfig }>(`/api/v1/integrations/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  })
}

export function useDeleteIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/integrations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  })
}

export function useTestIntegration() {
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { success: boolean; message?: string; error?: string } }>(
        `/api/v1/integrations/${id}/test`
      ),
  })
}

export function useSyncIntegration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { message: string } }>(`/api/v1/integrations/${id}/sync`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
  })
}

export function useIntegrationLogs(id: string, params?: { status?: string; action?: string; limit?: number }) {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set("status", params.status)
  if (params?.action) searchParams.set("action", params.action)
  if (params?.limit) searchParams.set("limit", String(params.limit))
  const qs = searchParams.toString()

  return useQuery({
    queryKey: ["integrations", id, "logs", params],
    queryFn: () =>
      api.get<{ data: IntegrationSyncLog[] }>(
        `/api/v1/integrations/${id}/logs${qs ? `?${qs}` : ""}`
      ),
    enabled: !!id,
  })
}
